import { createFileRoute, Link } from "@tanstack/react-router";

const guides = [
  {
    slug: "does-us-patent-protect-europe",
    title: "Does a US patent protect you in Europe?",
    body: "No — and the twelve- and thirty-one-month deadlines, the EPO's lack of a grace period and the EPO vs USPTO differences decide what is still filable.",
  },
  {
    slug: "cost-of-filing-a-european-patent",
    title: "European patent cost 2026",
    body: "The official EPO fee schedule, attorney fee ranges and three worked examples from filing to grant, plus EPO validation vs Unitary Patent.",
  },
];

import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Section } from "@/components/site/Section";
import { CTAButton, mailto } from "@/components/site/CTAButton";

export const Route = createFileRoute("/european-patents")({
  head: () => ({
    meta: [
      { title: "European Patent Attorney for US Companies (2026 Fees)" },
      {
        name: "description",
        content:
          "Talk directly to an EPO-registered attorney — no US middleman. 31-month PCT deadline, EPO fees from ~$5k, validation and UPC advice. Free 20-min case review.",
      },
      {
        property: "og:title",
        content: "European Patent Attorney for US Companies (2026 Fees)",
      },
      { property: "og:image", content: "https://bauerip.com/og-us.jpg" },
      { name: "twitter:image", content: "https://bauerip.com/og-us.jpg" },
      {
        property: "og:description",
        content:
          "Talk directly to an EPO-registered attorney — no US middleman. 31-month PCT deadline, EPO fees from ~$5k, validation and UPC advice. Free 20-min case review.",
      },

      { property: "og:url", content: "https://bauerip.com/european-patents" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://bauerip.com/european-patents" }],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "European patent counsel",
          provider: { "@type": "Person", name: "Karel Bauer" },
          areaServed: ["EP", "EU", "UPC"],
          audience: {
            "@type": "Audience",
            audienceType: "US deep-tech companies",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://bauerip.com",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "European patents",
              item: "https://bauerip.com/european-patents",
            },
          ],
        }),
      },
    ],

  }),
  component: PatentsPage,
});

const sectors = [
  "Photonics and lasers",
  "Materials",
  "Energy-related technologies",
  "Computer-implemented inventions",
  "Technically complex industrial systems",
];

const problems = [
  "Entering Europe through PCT without a clear EPO strategy.",
  "Weak claim adaptation for European practice.",
  "Pressure from prior art or examination objections.",
  "Opposition risk after grant.",
  "Portfolio decisions when budget matters.",
];

const services = [
  "EP validation strategy",
  "European prosecution",
  "PCT regional phase before the EPO",
  "Examination and amendment strategy",
  "EPO oppositions",
  "Patentability and filing support",
  "Support in technically complex disputes",
  "UPC representation",
];

const scenarios = [
  {
    title: "US startup entering Europe through PCT",
    body: "Claim strategy and prosecution before the EPO — adapted for European examination, not a copy of the US file.",
  },
  {
    title: "Deep-tech SME under pressure from prior art",
    body: "Amendment strategy and technical argumentation to keep meaningful scope through examination.",
  },
  {
    title: "Competitor patent creates risk",
    body: "Opposition support and European dispute positioning — including UPC considerations.",
  },
];

const faqs = [
  {
    q: "Should we enter Europe through the EPO?",
    a: "For most US applicants with European market interest, yes — one prosecution at the EPO covers most of Europe, with national validation and now the option of unitary effect.",
  },
  {
    q: "How is European examination different from the US?",
    a: "European examination places more weight on technical character, problem-and-solution analysis, and strict added-matter rules. Claims that worked in the US often need restructuring.",
  },
  {
    q: "When should we adapt claims for Europe?",
    a: "Ideally before regional phase entry. Late adaptation is possible but constrained — early planning protects scope.",
  },
  {
    q: "Is software patentable in Europe?",
    a: "Computer-implemented inventions are patentable in Europe when they produce a further technical effect. The framing matters as much as the underlying technology.",
  },
  {
    q: "What is an EPO opposition and when does it matter?",
    a: "Within nine months of grant, third parties can centrally challenge a European patent at the EPO. It is often the most cost-effective way to clear a blocking competitor patent — and a serious risk to defend against.",
  },
];

function PatentsPage() {
  const consultBody =
    "Hello Karel,\n\nI'd like to discuss European patent strategy.\n\n— Name:\n— Company:\n— Technology field:\n— Stage (filing, prosecution, opposition, UPC):\n— Brief description of the matter:\n";

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        {/* HERO */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-6xl px-6 pt-20 md:pt-28 pb-16 md:pb-20">
            <p className="eyebrow mb-6">For US Companies · EPO &amp; UPC</p>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-4xl">
              European Patent Attorney for US Companies — EPO &amp; UPC.
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-muted-foreground leading-relaxed">
              Get a European patent filed and prosecuted by a registered EPO
              attorney. Direct English-speaking counsel, no handoffs.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <CTAButton variant="accent"
                href={mailto({
                  subject: "European patent strategy",
                  body: consultBody,
                })}
              >
                Discuss Your European Patent Strategy
              </CTAButton>
              <CTAButton
                variant="secondary"
                href={mailto({
                  subject: "Book a call — European patents",
                  body: consultBody,
                })}
              >
                Book a Call
              </CTAButton>
            </div>
          </div>
        </section>

        {/* FOR WHOM + SECTORS */}
        <Section
          eyebrow="Who this page is for"
          title="US deep-tech companies seeking patent protection in Europe."
        >
          <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
            Founders, CTOs, in-house IP counsel and SMEs entering Europe
            through the EP or PCT route — particularly in complex technical
            fields.
          </p>
          <div className="mt-12">
            <p className="eyebrow mb-5">Sectors</p>
            <ul className="grid gap-3 md:grid-cols-2">
              {sectors.map((s) => (
                <li
                  key={s}
                  className="border-t border-border pt-3 text-foreground"
                >
                  <span className="text-accent-text mr-3">—</span>
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </Section>

        {/* PROBLEMS */}
        <div className="border-t border-border bg-secondary/30">
          <Section eyebrow="What's at stake" title="The problems I solve.">
            <ul className="grid gap-px md:grid-cols-2 bg-border">
              {problems.map((p, i) => (
                <li
                  key={i}
                  className="bg-background p-8 flex gap-5 text-foreground"
                >
                  <span className="font-serif text-accent-text text-xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>

        {/* SERVICES */}
        <Section eyebrow="Services" title="What I do for patent clients.">
          <ul className="grid gap-4 md:grid-cols-2">
            {services.map((s) => (
              <li
                key={s}
                className="border-t border-border pt-4 text-foreground"
              >
                <span className="text-accent-text mr-3">—</span>
                {s}
              </li>
            ))}
          </ul>
        </Section>

        {/* WHY ME */}
        <div className="border-y border-border bg-primary text-primary-foreground">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24 grid gap-12 md:grid-cols-[1fr_1.4fr]">
            <div>
              <p className="eyebrow mb-4" style={{ color: "var(--accent)" }}>
                Why work with me
              </p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight">
                A physics background for technically demanding work.
              </h2>
            </div>
            <div className="space-y-6 text-primary-foreground/90 leading-relaxed text-lg">
              <p>
                My background is in physics, with particular depth in
                technically demanding subject matter. I work comfortably across
                complex inventions and communicate directly with inventors,
                engineers and founders.
              </p>
              <ul className="grid gap-3 text-base">
                <li className="border-t border-primary-foreground/15 pt-3">
                  <span className="text-accent mr-3">—</span> European Patent
                  Attorney with own established practice
                </li>
                <li className="border-t border-primary-foreground/15 pt-3">
                  <span className="text-accent mr-3">—</span> Representative
                  before the Unified Patent Court
                </li>
                <li className="border-t border-primary-foreground/15 pt-3">
                  <span className="text-accent mr-3">—</span> Direct
                  communication with technical teams
                </li>
                <li className="border-t border-primary-foreground/15 pt-3">
                  <span className="text-accent mr-3">—</span> Practical advice,
                  not boilerplate
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* USE CASES */}
        <Section
          eyebrow="Typical use cases"
          title="Three situations I work on most."
        >
          <div className="grid gap-px md:grid-cols-3 bg-border">
            {scenarios.map((s, i) => (
              <div key={s.title} className="bg-background p-8">
                <p className="font-serif text-accent-text text-2xl">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-4 font-serif text-xl text-foreground leading-snug">
                  {s.title}
                </h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* GUIDES */}
        <div className="border-t border-border">
          <Section eyebrow="Guides" title="Start with these two guides.">
            <div className="grid gap-px md:grid-cols-2 bg-border">
              {guides.map((g) => (
                <Link
                  key={g.slug}
                  to="/blog/$slug"
                  params={{ slug: g.slug }}
                  className="bg-background p-8 group"
                >
                  <h3 className="font-serif text-xl text-foreground leading-snug group-hover:text-accent-text transition-colors">
                    {g.title}
                  </h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">
                    {g.body}
                  </p>
                  <span className="mt-4 inline-block text-sm text-accent-text">
                    Read the guide →
                  </span>
                </Link>
              ))}
            </div>
          </Section>
        </div>

        {/* PILLAR LINK */}
        <div className="border-t border-border bg-secondary/30">
          <Section
            eyebrow="Services & fees"
            title="Scope, process and stage-by-stage fees."
            intro="See exactly what an engagement covers, how the five-stage process runs, and what each stage costs."
          >
            <Link
              to="/services/european-patent-attorney"
              className="inline-flex items-center text-lg text-accent-text hover:underline"
            >
              European patent prosecution services for US applicants →
            </Link>

            <p className="mt-8 text-muted-foreground leading-relaxed max-w-3xl">
              Planning a new European filing?{" "}
              <Link to="/services/pct-european-phase-entry" className="underline underline-offset-4">
                PCT European phase entry — deadline, costs and requirements
              </Link>
              .<br />
              Single-issue pages:{" "}
              <Link to="/tools/epo-renewal-fees" className="underline underline-offset-4">
                EPO renewal fees and annuities
              </Link>
              ,{" "}
              <Link
                to="/tools/european-patent-validation-costs"
                className="underline underline-offset-4"
              >
                European patent validation costs by country
              </Link>{" "}
              and{" "}
              <Link
                to="/services/epo-office-action-response"
                className="underline underline-offset-4"
              >
                EPO office action responses
              </Link>
              .
            </p>
          </Section>

        </div>





        {/* FAQ */}
        <div className="border-t border-border bg-secondary/30">
          <Section eyebrow="FAQ" title="Common questions.">
            <dl className="divide-y divide-border border-y border-border">
              {faqs.map((f) => (
                <div
                  key={f.q}
                  className="grid md:grid-cols-[1fr_2fr] gap-6 py-8"
                >
                  <dt className="font-serif text-lg text-foreground">{f.q}</dt>
                  <dd className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </dd>
                </div>
              ))}
            </dl>
          </Section>
        </div>

        {/* CTA */}
        <section className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
            <h2 className="font-serif text-3xl md:text-5xl text-foreground leading-tight max-w-3xl">
              Need a practical European patent strategy?
            </h2>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              Let&rsquo;s discuss your filing, prosecution or opposition
              options — including UPC implications.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <CTAButton variant="accent"
                href={mailto({
                  subject: "Review my EPO filing plan",
                  body: consultBody,
                })}
              >
                Review Your EPO Filing Plan
              </CTAButton>
              <CTAButton
                variant="secondary"
                href={mailto({
                  subject: "Book a call — European patents",
                  body: consultBody,
                })}
              >
                Book a Call
              </CTAButton>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
